import React, { useEffect, useRef } from 'react';

// Subtle animated hexagon grid background reacting to mouse/touch
export default function HexBackground(props) {
  // allow overrides via CSS variables on :root as well as props
  const cssBase = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-base'));
  const cssGap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-gap'));
  const cssMaxRot = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-rotate-max'));
  const cssDelay = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-delay-ms'));
  const cssNoise = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-noise'));
  const cssNoiseSpeed = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hex-noise-speed'));
  const baseProp = props.base;
  const gapProp = props.gap;
  const fixedProp = props.fixed;
  const maxRotateProp = props.maxRotate;
  const delayProp = props.delayMs;
  const noiseProp = props.noise;
  const noiseSpeedProp = props.noiseSpeed;
  const base = (typeof baseProp === 'number' && baseProp > 0)
    ? baseProp
    : (!Number.isNaN(cssBase) && cssBase > 0 ? cssBase : 12);
  const gap = (typeof gapProp === 'number' && gapProp > 0)
    ? gapProp
    : (!Number.isNaN(cssGap) && cssGap > 0 ? cssGap : 1);
  const fixed = fixedProp ?? false; // base hex radius, gap multiplier, fixed covers viewport
  const maxRotateDeg = (typeof maxRotateProp === 'number' && maxRotateProp >= 0)
    ? maxRotateProp
    : (!Number.isNaN(cssMaxRot) && cssMaxRot >= 0 ? cssMaxRot : 33);
  const maxRotateRad = (Math.PI / 180) * maxRotateDeg;
  const delayMs = (typeof delayProp === 'number' && delayProp >= 0)
    ? delayProp
    : (!Number.isNaN(cssDelay) && cssDelay >= 0 ? cssDelay : 450);
  const noiseAmp = (typeof noiseProp === 'number' && noiseProp >= 0)
    ? noiseProp
    : (!Number.isNaN(cssNoise) && cssNoise >= 0 ? cssNoise : 0.18);
  const noiseSpeed = (typeof noiseSpeedProp === 'number' && noiseSpeedProp >= 0)
    ? noiseSpeedProp
    : (!Number.isNaN(cssNoiseSpeed) && cssNoiseSpeed >= 0 ? cssNoiseSpeed : 0.0008);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const gridRef = useRef({ cells: [], cols: 0, rows: 0, base });
  const ripplesRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let reduceMotion = false;
    try {
      reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {}

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      const parent = canvas.parentElement;
      const w = fixed ? window.innerWidth : parent.clientWidth;
      const h = fixed ? window.innerHeight : Math.max(parent.clientHeight, 300);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildGrid(w, h);
    }

    function buildGrid(w, h) {
      const s = gridRef.current.base; // hex radius
      const spacingX = s * 1.5 + gap; // exact flat-top neighbor center distance plus gap
      const spacingY = Math.sqrt(3) * s + gap; // exact row step plus gap
      const cols = Math.ceil(w / spacingX) + 2;
      const rows = Math.ceil(h / spacingY) + 2;
      const cells = [];
      for (let r = 0; r < rows; r++) {
        const rowOffsetX = (r % 2) ? spacingX / 2 : 0; // stagger
        const y = r * spacingY;
        for (let c = 0; c < cols; c++) {
          const x = c * spacingX + rowOffsetX;
          cells.push({ x, y, scale: 1, influence: 0, lastHitAt: 0, phase: Math.random() * Math.PI * 2, amp: noiseAmp * (0.6 + Math.random() * 0.8) });
        }
      }
      gridRef.current = { cells, cols, rows, base: s };
    }

    function hexPath(x, y, r, rot) {
      const a = (Math.PI * 2) / 6;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const theta = a * i + rot;
        const px = x + r * Math.cos(theta);
        const py = y + r * Math.sin(theta);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    }

    function draw() {
      const { cells } = gridRef.current;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);

      const primary = getComputedStyle(document.documentElement).getPropertyValue('--border') || '#94a3b8';
      ctx.strokeStyle = primary.trim();

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const active = mouseRef.current.active;
      const influenceRadius = Math.max(120, gridRef.current.base * 10); // scale with hex size
      // Ripple parameters
      const RIPPLE_DURATION = 3000; // ms
      const RIPPLE_MAX_RADIUS = Math.max(w, h) * 1; // px
      const RIPPLE_BAND = 36; // px ring thickness affecting cells
      const RIPPLE_STROKE = 0; // px
      const RIPPLE_COLOR = primary.trim();
      const RIPPLE_STRENGTH = 3.6; // 0..1 hex influence multiplier
      const now = performance.now();

      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        // Update influence by proximity
        if (active) {
          const dx = mx - cell.x;
          const dy = my - cell.y;
          const dist = Math.hypot(dx, dy);
          if (dist < influenceRadius) {
            const k = 1 - dist / influenceRadius; // 0..1
            const newInf = Math.max(cell.influence, k);
            if (newInf > cell.influence) cell.lastHitAt = now;
            cell.influence = newInf;
          }
        }
        // Add ripple influence
        if (ripplesRef.current.length) {
          for (let r = 0; r < ripplesRef.current.length; r++) {
            const rp = ripplesRef.current[r];
            const age = now - rp.t0;
            if (age >= 0 && age <= RIPPLE_DURATION) {
              const radius = (age / RIPPLE_DURATION) * RIPPLE_MAX_RADIUS;
              const d = Math.hypot(cell.x - rp.x, cell.y - rp.y);
              const diff = Math.abs(d - radius);
              if (diff < RIPPLE_BAND) {
                const k = 1 - diff / RIPPLE_BAND; // 0..1
                const fade = 1 - age / RIPPLE_DURATION; // 1..0
                const newInf = Math.max(cell.influence, k * RIPPLE_STRENGTH * fade);
                if (newInf > cell.influence) cell.lastHitAt = now;
                cell.influence = newInf;
              }
            }
          }
        }

        // decay influence for trail after adjustable delay
        if (cell.influence > 0) {
          const elapsed = now - cell.lastHitAt;
          if (elapsed > delayMs) {
            cell.influence *= 0.92;
            if (cell.influence < 0.001) cell.influence = 0;
          }
        }
        const n = Math.max(0, Math.sin(now * noiseSpeed + cell.phase)) * cell.amp; // 0..amp
        const effective = Math.min(1, cell.influence + n);
        const targetScale = 1 - 0.45 * effective;
        cell.scale += (targetScale - cell.scale) * 0.12; // ease back with spring feel

        const r = Math.max(1.5, gridRef.current.base * cell.scale);
        const rot = maxRotateRad * effective;
        hexPath(cell.x, cell.y, r, rot);
        // Thicken stroke near pointer
        ctx.lineWidth = 0.8 + 1.6 * effective;
        ctx.stroke();
      }

      // Draw ripple rings (only if stroke > 0)
      if (RIPPLE_STROKE > 0 && ripplesRef.current.length) {
        ctx.save();
        ctx.strokeStyle = RIPPLE_COLOR;
        for (let i = ripplesRef.current.length - 1; i >= 0; i--) {
          const rp = ripplesRef.current[i];
          const age = now - rp.t0;
          if (age > RIPPLE_DURATION) { ripplesRef.current.splice(i, 1); continue; }
          const p = age / RIPPLE_DURATION;
          const radius = p * RIPPLE_MAX_RADIUS;
          ctx.globalAlpha = Math.max(0, 1 - p) * 0.5;
          ctx.lineWidth = RIPPLE_STROKE;
          ctx.beginPath();
          ctx.arc(rp.x, rp.y, radius, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.restore();
      }

      if (!reduceMotion) rafRef.current = requestAnimationFrame(draw);
    }

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      mouseRef.current.x = clientX - rect.left;
      mouseRef.current.y = clientY - rect.top;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    const onWindowMouseOut = (e) => {
      // If leaving the window entirely
      if (!e.relatedTarget && !e.toElement) {
        mouseRef.current.active = false;
      }
    };

    // keep in sync with props and rebuild grid proactively
    gridRef.current.base = base;
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: true });
    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.clientX ?? (e.touches ? e.touches[0].clientX : 0);
      const clientY = e.clientY ?? (e.touches ? e.touches[0].clientY : 0);
      ripplesRef.current.push({ x: clientX - rect.left, y: clientY - rect.top, t0: performance.now() });
    };
    window.addEventListener('click', onClick);
    window.addEventListener('mouseout', onWindowMouseOut);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('touchend', onLeave);

    if (!reduceMotion) rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('click', onClick);
      window.removeEventListener('mouseout', onWindowMouseOut);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('touchend', onLeave);
    };
  }, [base, gap, fixed, maxRotateRad, delayMs, noiseAmp, noiseSpeed]);

  return (
    <canvas
      ref={canvasRef}
      className="hex-bg-canvas"
      aria-hidden
    />
  );
}


